import { v4 as uuidv4 } from 'uuid';

const WRAP_TEMPLATE_BLOCKLIST = new Set([
  'entryTactics',
  'positionTactics',
  'setup',
  'setupDetails',
  'setupFilters',
  'restartSettings',
  'restartSystemOncePositionHasFlattened',
  'startPeriod',
  'startHour',
  'startMin',
  'optionManagement',
  'quantityBasis',
  'goLong',
  'goShort',
  'symbol',
  'instrument',
  'accountNumber',
  'brokerage',
  'dataSource',
  'evaluateQuotes',
  'persistTradePlanOnFailedFiltersOrValidation',
  'workOrdersOnEntry',
]);

export const WRAP_SOURCE_MODE = {
  POSITIONS: 'positions',
  ORDERS: 'orders',
};

export function buildWrapContextKey({ brokerage, accountNumber }) {
  return `${brokerage ?? 'none'}|${accountNumber ?? 'none'}`;
}

export function createWrapRequestId() {
  return `wrap-${uuidv4()}`;
}

export function sanitizeTemplateForWrap(template = {}) {
  const sanitized = { ...template };
  WRAP_TEMPLATE_BLOCKLIST.forEach((field) => {
    delete sanitized[field];
  });

  sanitized.flattenAtMarketClose = Boolean(template.flattenAtMarketClose);
  sanitized.workOrders = Boolean(template.workOrders);
  sanitized.workOrdersOnExit =
    template.workOrdersOnExit !== undefined
      ? Boolean(template.workOrdersOnExit)
      : true;
  sanitized.useDifferentWorkOrderFieldsOnExit = Boolean(
    template.useDifferentWorkOrderFieldsOnExit
  );
  sanitized.useDifferentWorkOrderFieldsOnTarget = Boolean(
    template.useDifferentWorkOrderFieldsOnTarget
  );
  sanitized.exitTactics = template.exitTactics ?? {};
  sanitized.savedConfigurationTitle = template.savedConfigurationTitle ?? '';
  return sanitized;
}

export function getRowRemainingQuantity(row) {
  if (!row) return 0;
  if (row.sourceType === 'order') {
    return Number(row.signedRemainingQuantity ?? 0);
  }
  return Number(row.signedRemainingQuantity ?? 0);
}

export function getBucketRemainingQuantity(bucket, sourceMode) {
  if (!bucket) return 0;
  return Number(bucket.signedRemainingQuantity ?? 0);
}

export function initializeSelectionQuantity(row, sourceMode, bucket) {
  const rowRemaining = getRowRemainingQuantity(row);
  if (sourceMode !== WRAP_SOURCE_MODE.ORDERS) {
    return rowRemaining;
  }

  return rowRemaining;
}

export function clearSelectionsForModeSwitch() {
  return {
    selectedRows: {},
    quantitiesById: {},
  };
}

export function deriveWrapBuckets(response) {
  const bucketMap = {};
  (response?.buckets ?? []).forEach((bucket) => {
    bucketMap[bucket.bucketKey] = bucket;
  });
  return bucketMap;
}

function compareRowsChronologically(left, right, newestFirst = false) {
  const leftTime = new Date(left?.transactionDate || left?.createDate || 0).getTime();
  const rightTime = new Date(right?.transactionDate || right?.createDate || 0).getTime();
  if (leftTime !== rightTime) {
    return newestFirst ? rightTime - leftTime : leftTime - rightTime;
  }
  return String(left?.sourceId || left?.candidateId || '').localeCompare(
    String(right?.sourceId || right?.candidateId || '')
  );
}

function calculateOrderFifoPreview(rows, quantitiesById, direction, priceMultiplier) {
  const lots = [];
  let realizedPnl = 0;
  const isShort = direction === 'SHORT';

  rows.forEach((row) => {
    const signedQuantity = Number(quantitiesById?.[row.candidateId] ?? 0);
    const price = Number(row.entryPrice ?? row.avgFillPrice ?? 0);
    if (!signedQuantity || !price) {
      return;
    }

    const absoluteQuantity = Math.abs(signedQuantity);
    const isOpeningTrade = isShort ? signedQuantity < 0 : signedQuantity > 0;
    if (isOpeningTrade) {
      lots.push({ quantity: absoluteQuantity, price });
      return;
    }

    let remainingToClose = absoluteQuantity;
    while (remainingToClose > 0 && lots.length > 0) {
      const currentLot = lots[0];
      const closedQuantity = Math.min(currentLot.quantity, remainingToClose);
      const priceDelta = isShort ? currentLot.price - price : price - currentLot.price;
      realizedPnl += priceDelta * closedQuantity * priceMultiplier;
      currentLot.quantity -= closedQuantity;
      remainingToClose -= closedQuantity;
      if (currentLot.quantity <= 0.000001) {
        lots.shift();
      }
    }
  });

  const openQuantityAbsolute = lots.reduce((total, lot) => total + lot.quantity, 0);
  const openEntryValue = lots.reduce(
    (total, lot) => total + lot.quantity * lot.price,
    0
  );

  return {
    openQuantityAbsolute,
    openEntryPrice:
      openQuantityAbsolute > 0 ? openEntryValue / openQuantityAbsolute : null,
    realizedPnl,
  };
}

export function toggleWrapSelection({
  row,
  selectedRows,
  quantitiesById,
  sourceMode,
  bucket,
}) {
  const nextSelectedRows = { ...selectedRows };
  const nextQuantitiesById = { ...quantitiesById };
  const alreadySelected = Boolean(nextSelectedRows[row.candidateId]);

  if (alreadySelected) {
    delete nextSelectedRows[row.candidateId];
    delete nextQuantitiesById[row.candidateId];
    return {
      selectedRows: nextSelectedRows,
      quantitiesById: nextQuantitiesById,
    };
  }

  nextSelectedRows[row.candidateId] = row;
  nextQuantitiesById[row.candidateId] = initializeSelectionQuantity(
    row,
    sourceMode,
    bucket
  );

  return {
    selectedRows: nextSelectedRows,
    quantitiesById: nextQuantitiesById,
  };
}

export function summarizeSelection({
  selectedRows,
  quantitiesById,
}) {
  const rows = Object.values(selectedRows);
  const selectedBucketKey = rows[0]?.bucketKey ?? '';
  const signedQuantity = rows.reduce(
    (total, row) => total + Number(quantitiesById[row.candidateId] ?? 0),
    0
  );

  return {
    rows,
    selectedBucketKey,
    signedQuantity,
  };
}

export function validateWrapSelection({
  sourceMode,
  selectedRows,
  quantitiesById,
  bucketsByKey,
}) {
  const rows = Object.values(selectedRows);
  if (rows.length === 0) {
    return {
      valid: false,
      message: 'Select at least one source row to wrap.',
    };
  }

  const bucketKey = rows[0].bucketKey;
  if (!rows.every((row) => row.bucketKey === bucketKey)) {
    return {
      valid: false,
      message: 'All selected rows must belong to the same symbol/account/direction bucket.',
    };
  }

  const bucket = bucketsByKey[bucketKey];
  const aggregateSignedQuantity = rows.reduce((total, row) => {
    return total + Number(quantitiesById[row.candidateId] ?? 0);
  }, 0);

  if (aggregateSignedQuantity === 0) {
    return {
      valid: false,
      message: 'Wrapped quantity must be non-zero.',
    };
  }

  const expectedDirection = rows[0].direction;
  const expectedSign = expectedDirection === 'SHORT' ? -1 : 1;

  for (const row of rows) {
    const quantity = Number(quantitiesById[row.candidateId] ?? 0);
    const rowRemaining = getRowRemainingQuantity(row);

    if (quantity === 0) {
      return {
        valid: false,
        message: 'Each selected row must have a non-zero signed quantity.',
      };
    }

    if (
      sourceMode !== WRAP_SOURCE_MODE.ORDERS &&
      Math.sign(quantity) !== Math.sign(expectedSign)
    ) {
      return {
        valid: false,
        message: 'Selected quantities must keep the original long/short direction.',
      };
    }

    if (Math.abs(quantity) > Math.abs(rowRemaining)) {
      return {
        valid: false,
        message: `Selected quantity exceeds the remaining available quantity for ${row.symbol}.`,
      };
    }
  }

  const bucketRemaining =
    sourceMode === WRAP_SOURCE_MODE.ORDERS
      ? Number(rows[0].bucketSignedRemainingQuantity ?? 0)
      : getBucketRemainingQuantity(bucket, sourceMode);

  if (
    bucketRemaining !== 0 &&
    Math.sign(aggregateSignedQuantity) !== Math.sign(bucketRemaining)
  ) {
    return {
      valid: false,
      message: 'Selected orders must net to the live position direction.',
    };
  }

  if (Math.abs(aggregateSignedQuantity) > Math.abs(bucketRemaining)) {
    return {
      valid: false,
      message: 'Selected quantity exceeds the remaining quantity available for this position.',
    };
  }

  return {
    valid: true,
    message: '',
    bucketKey,
    aggregateSignedQuantity,
  };
}

export function buildWrappedTradePlanPayload({
  tradePlan,
  sourceMode,
  selectedRows,
  quantitiesById,
}) {
  const rows = Object.values(selectedRows);
  const firstRow = rows[0];
  const signedQuantity = rows.reduce(
    (total, row) => total + Number(quantitiesById[row.candidateId] ?? 0),
    0
  );

  return {
    tradePlan,
    wrapSource: {
      sourceMode,
      bucketKey: firstRow.bucketKey,
      symbol: firstRow.symbol,
      brokerage: firstRow.brokerage,
      accountNumber: firstRow.accountNumber,
      direction: firstRow.direction,
      signedQuantity,
      selections: rows.map((row) => ({
        candidateId: row.candidateId,
        sourceType: row.sourceType,
        sourceId: row.sourceId,
        signedQuantity: Number(quantitiesById[row.candidateId] ?? 0),
      })),
    },
  };
}

export function calculateWrapPreview({
  selectedRows,
  quantitiesById,
  bucketsByKey,
}) {
  const rows = Object.values(selectedRows ?? {});
  if (rows.length === 0) {
    return {
      hasPreview: false,
      signedQuantity: 0,
      entryPrice: null,
      valuationPrice: null,
      unrealizedPnl: null,
      percentPnl: null,
      realizedPnl: null,
      priceMultiplier: null,
      valuationSource: '',
      bid: null,
      ask: null,
      lastPrice: null,
    };
  }

  const bucket = bucketsByKey?.[rows[0].bucketKey] ?? rows[0];
  const priceMultiplier = Number(bucket?.priceMultiplier ?? rows[0]?.priceMultiplier ?? 0);
  const chronologicalRows = [...rows].sort((left, right) =>
    compareRowsChronologically(left, right, false)
  );
  const signedQuantity = rows.reduce(
    (total, row) => total + Number(quantitiesById?.[row.candidateId] ?? 0),
    0
  );
  const absoluteQuantity = Math.abs(signedQuantity);
  if (absoluteQuantity === 0) {
    return {
      hasPreview: false,
      signedQuantity: 0,
      entryPrice: null,
      valuationPrice: Number(bucket?.valuationPrice ?? 0) || null,
      unrealizedPnl: 0,
      percentPnl: null,
      realizedPnl: null,
      priceMultiplier: priceMultiplier || null,
      valuationSource: bucket?.valuationSource ?? '',
      bid: Number(bucket?.bid ?? 0) || null,
      ask: Number(bucket?.ask ?? 0) || null,
      lastPrice: Number(bucket?.lastPrice ?? 0) || null,
    };
  }

  const valuationPrice = Number(bucket?.valuationPrice ?? 0);
  const direction = rows[0]?.direction ?? 'LONG';
  const isOrderMode = rows[0]?.sourceType === 'order';
  let entryPrice = null;
  let realizedPnl = null;

  if (isOrderMode) {
    const fifoPreview = calculateOrderFifoPreview(
      chronologicalRows,
      quantitiesById,
      direction,
      priceMultiplier || 1
    );
    entryPrice = fifoPreview.openEntryPrice;
    realizedPnl = fifoPreview.realizedPnl;
  } else {
    const bucketEntryPrice = Number(bucket?.entryPrice ?? 0);
    const weightedEntryValue = rows.reduce((total, row) => {
      const qty = Math.abs(Number(quantitiesById?.[row.candidateId] ?? 0));
      const rowEntryPrice = Number(row.entryPrice ?? row.avgFillPrice ?? 0);
      return total + qty * rowEntryPrice;
    }, 0);
    entryPrice =
      bucketEntryPrice > 0 ? bucketEntryPrice : weightedEntryValue / absoluteQuantity;
  }

  const hasValuation = Number.isFinite(valuationPrice) && valuationPrice > 0;
  const hasMultiplier = Number.isFinite(priceMultiplier) && priceMultiplier > 0;
  const hasEntry = Number.isFinite(entryPrice) && entryPrice > 0;

  let unrealizedPnl = null;
  let percentPnl = null;
  if (hasValuation && hasMultiplier && hasEntry) {
    const priceDelta =
      direction === 'SHORT'
        ? entryPrice - valuationPrice
        : valuationPrice - entryPrice;
    unrealizedPnl = priceDelta * absoluteQuantity * priceMultiplier;
    const costBasis = entryPrice * absoluteQuantity * priceMultiplier;
    percentPnl = costBasis > 0 ? (unrealizedPnl / costBasis) * 100 : null;
  }

  return {
    hasPreview: hasValuation && hasMultiplier && hasEntry,
    signedQuantity,
    entryPrice: hasEntry ? entryPrice : null,
    valuationPrice: hasValuation ? valuationPrice : null,
    unrealizedPnl,
    percentPnl,
    realizedPnl,
    priceMultiplier: hasMultiplier ? priceMultiplier : null,
    valuationSource: bucket?.valuationSource ?? '',
    bid: Number(bucket?.bid ?? 0) || null,
    ask: Number(bucket?.ask ?? 0) || null,
    lastPrice: Number(bucket?.lastPrice ?? 0) || null,
  };
}
